import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Button } from '../ui/button';
import { useApp } from '../../context/AppContext';
import { api } from '../../api';
import { generateReportPDF, generateInvoicePDF } from '../../utils/pdf';
import LiveMap from '../shared/LiveMap';

const Stat = ({ icon, label, value, tone }) => {
  const C = Icons[icon] || Icons.Circle;
  const map = { amber: 'bg-amber-50 text-amber-600', blue: 'bg-blue-50 text-blue-600', emerald: 'bg-emerald-50 text-emerald-600', navy: 'bg-[#eaf1fb] text-[#1c63c4]' };
  return <div className="bg-white rounded-2xl border border-border p-5"><div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 ${map[tone]}`}><C className="h-5 w-5" /></div><div className="text-2xl font-display font-extrabold text-[#102d55]">{value}</div><div className="text-sm text-muted-foreground">{label}</div></div>;
};

const Timeline = ({ items }) => (
  <div className="mt-3 space-y-2">
    {items.map((t, i) => (
      <div key={i} className="flex items-start gap-2.5">
        <div className="flex flex-col items-center"><span className="h-2 w-2 rounded-full bg-[#1c63c4] mt-1" />{i < items.length - 1 && <span className="w-px flex-1 bg-border min-h-[14px]" />}</div>
        <div className="text-xs"><span className="font-medium text-[#102d55] capitalize">{t.state.replace(/_/g, ' ')}</span><span className="text-muted-foreground"> — {t.note}</span></div>
      </div>
    ))}
  </div>
);

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  const [jobs, setJobs] = useState([]);
  const [openTl, setOpenTl] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);

  const load = () => api.get('/jobs').then((r) => setJobs(r.data));
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

  const tracked = jobs.filter((j) => j.tracking_active && j.contractor_location);

  const cols = [
    { key: 'pending', title: 'Pending Jobs', icon: 'Clock' },
    { key: 'in_progress', title: 'Jobs in Progress', icon: 'Loader' },
    { key: 'completed', title: 'Completed Jobs', icon: 'CheckCircle2' },
  ];
  const byGroup = (g) => jobs.filter((j) => j.group === g);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div><h1 className="font-display font-extrabold text-2xl text-[#102d55]">Welcome back, {user.name.split(' ')[0]}</h1><p className="text-muted-foreground">{user.org} · Landlord / Agent workspace</p></div>
        <Button onClick={() => navigate('/book')} className="bg-[#102d55] hover:bg-[#0b2244]"><Icons.Plus className="h-4 w-4 mr-1.5" /> New booking</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon="ClipboardList" label="Total jobs" value={jobs.length} tone="navy" />
        <Stat icon="Clock" label="Pending" value={byGroup('pending').length} tone="amber" />
        <Stat icon="Loader" label="In progress" value={byGroup('in_progress').length} tone="blue" />
        <Stat icon="CheckCircle2" label="Completed" value={byGroup('completed').length} tone="emerald" />
      </div>
      {tracked.length > 0 && (
        <div className="bg-white rounded-2xl border border-border p-5 mb-8">
          <div className="flex items-center gap-2 mb-4"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" /><h2 className="font-display font-semibold text-[#102d55]">Live contractor tracking</h2><span className="text-xs text-muted-foreground">({tracked.length} active)</span></div>
          <div className="grid lg:grid-cols-2 gap-4">
            {tracked.map((job) => (
              <div key={job.id} className="border border-border rounded-xl p-3">
                <div className="flex items-center justify-between mb-2 px-1"><div className="min-w-0"><div className="text-sm font-medium text-[#102d55] truncate">{job.service_name} · {job.id}</div><div className="text-xs text-muted-foreground truncate">{job.assigned_contractor_name} → {job.property_address}</div></div><span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 capitalize">{job.status.replace(/_/g, ' ')}</span></div>
                <LiveMap property={job.property_coords} contractor={job.contractor_location} history={job.location_history} height={220} />
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-5">
        {cols.map((col) => { const C = Icons[col.icon]; const list = byGroup(col.key); return (
          <div key={col.key} className="bg-secondary/40 rounded-2xl border border-border p-4">
            <div className="flex items-center gap-2 mb-4 px-1"><C className="h-4 w-4 text-[#1c63c4]" /><h2 className="font-display font-semibold text-[#102d55]">{col.title}</h2><span className="ml-auto text-xs font-semibold bg-white border border-border rounded-full px-2 py-0.5">{list.length}</span></div>
            <div className="space-y-3">
              {list.length === 0 && <p className="text-sm text-muted-foreground px-1 py-6 text-center">No jobs here.</p>}
              {list.map((job) => (
                <div key={job.id} className="bg-white rounded-xl border border-border p-4">
                  <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="font-semibold text-sm text-[#102d55] truncate">{job.service_name}</div><div className="text-xs text-muted-foreground truncate">{job.property_address}</div></div>
                    <span className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#eaf1fb] text-[#1c63c4] capitalize">{job.status.replace(/_/g, ' ')}</span></div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground"><span className="flex items-center gap-1"><Icons.Hash className="h-3 w-3" />{job.id}</span><span className="flex items-center gap-1"><Icons.Calendar className="h-3 w-3" />{job.scheduled_date}</span><span className="flex items-center gap-1"><Icons.User className="h-3 w-3" />{job.assigned_contractor_name || 'Unassigned'}</span></div>
                  <button onClick={() => setOpenTl(openTl === job.id ? null : job.id)} className="mt-2 text-xs font-medium text-[#1c63c4] flex items-center gap-1"><Icons.GitCommitHorizontal className="h-3.5 w-3.5" /> Tracking {openTl === job.id ? <Icons.ChevronUp className="h-3 w-3" /> : <Icons.ChevronDown className="h-3 w-3" />}</button>
                  {openTl === job.id && <Timeline items={job.timeline || []} />}
                  {job.group === 'completed' && (
                    <div className="mt-3 pt-3 border-t border-border space-y-2">
                      <div className="flex gap-2"><Button size="sm" variant="outline" className="flex-1" onClick={() => generateReportPDF(job)}><Icons.FileText className="h-3.5 w-3.5 mr-1" /> Report</Button><Button size="sm" variant="outline" className="flex-1" onClick={() => generateInvoicePDF(job)}><Icons.Receipt className="h-3.5 w-3.5 mr-1" /> Invoice</Button></div>
                      <button onClick={() => setOpenDoc(openDoc === job.id ? null : job.id)} className="text-xs font-medium text-[#1c63c4] flex items-center gap-1"><Icons.FolderOpen className="h-3.5 w-3.5" /> {(job.compliance || []).length} compliance doc(s)</button>
                      {openDoc === job.id && (job.compliance || []).map((d) => <button key={d} onClick={() => generateReportPDF(job)} className="w-full flex items-center gap-2 text-xs bg-secondary/50 border border-border rounded-lg px-3 py-2 hover:border-[#1c63c4]"><Icons.FileCheck2 className="h-3.5 w-3.5 text-emerald-600" /><span className="flex-1 truncate text-left">{d}</span><Icons.Download className="h-3.5 w-3.5 text-slate-400" /></button>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ); })}
      </div>
    </div>
  );
};

export default ManagerDashboard;
